// src/components/QuizComponent.js - HYBRIDE LOOKUP VERSION
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { 
  doc, 
  getDoc, 
  collection, 
  getDocs, 
  updateDoc, 
  addDoc,
  query,
  where 
} from 'firebase/firestore';

const QuizComponent = () => {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  
  // States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invitation, setInvitation] = useState(null);
  const [teamInfo, setTeamInfo] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);

  // Quiz configuration
  const tredes = ['vertrouwen', 'conflicten', 'betrokkenheid', 'verantwoording', 'resultaten'];
  const tredeNames = {
    'vertrouwen': 'Vertrouwen',
    'conflicten': 'Conflicten',
    'betrokkenheid': 'Commitment', //Veranderd van Betrokkenheid
    'verantwoording': 'Verantwoording',
    'resultaten': 'Resultaten'
  };

  // Load quiz data on component mount
  useEffect(() => {
    loadQuizData();
  }, [inviteCode]);

  const loadQuizData = async () => {
    try {
      setLoading(true);
      
      console.log('🔍 Searching for invite code (HYBRIDE):', inviteCode);

      // ✅ HYBRIDE STEP 1: Fast lookup via index
      let foundIndexEntry = null;
      
      const indexQuery = query(
        collection(db, 'invitations_index'), 
        where('code', '==', inviteCode)
      );
      const indexSnapshot = await getDocs(indexQuery);
      
      if (!indexSnapshot.empty) {
        foundIndexEntry = indexSnapshot.docs[0].data();
        console.log('✅ Found in index:', foundIndexEntry);
      } else {
        console.log('⚠️ Not found in index, falling back to legacy search...');
        
        // FALLBACK: Legacy search for old invitations
        const legacyResult = await legacyInviteSearch(inviteCode);
        if (!legacyResult) {
          setError('Ongeldige uitnodigingscode.');
          return;
        }
        foundIndexEntry = legacyResult;
      }

      // ✅ HYBRIDE STEP 2: Get full invitation data from subcollection
      const inviteRef = doc(db, 'teams', foundIndexEntry.teamId, 'invites', foundIndexEntry.inviteId);
      const inviteDoc = await getDoc(inviteRef);
      
      if (!inviteDoc.exists()) {
        setError('Uitnodiging niet gevonden.');
        return;
      }
      
      const inviteData = inviteDoc.data();
      console.log('✅ Found full invitation data:', inviteData);
      
      // Check if already used
      if (inviteData.used === true) {
        setError('Deze uitnodigingscode is al gebruikt.');
        return;
      }

      setInvitation({ 
        id: foundIndexEntry.inviteId, 
        teamId: foundIndexEntry.teamId,
        ...inviteData 
      });

      // 3. Get team information
      const teamDoc = await getDoc(doc(db, 'teams', foundIndexEntry.teamId));
      if (!teamDoc.exists()) {
        setError('Team niet gevonden.');
        return;
      }
      setTeamInfo({ id: teamDoc.id, ...teamDoc.data() });

      // 4. Load all questions from tredes collection
      console.log('📖 Loading questions from tredes collection...');
      const allQuestions = [];
      for (const trede of tredes) {
        console.log('📚 Loading trede:', trede);
        const tredeDoc = await getDoc(doc(db, 'tredes', trede));
        if (tredeDoc.exists()) {
          const tredeData = tredeDoc.data();
          
          if (tredeData.questions) {
            console.log('✅ Found', tredeData.questions.length, 'questions for', trede);
            tredeData.questions.forEach((question, index) => {
              const mappedQuestion = {
                vraag: question.text,
                type: question.type === 'scale' ? 'schaal' : 'scenario',
                opties: question.options ? question.options.map(opt => ({
                  tekst: opt.text,
                  waarde: opt.value
                })) : undefined,
                trede: trede,
                tredeIndex: tredes.indexOf(trede),
                questionIndex: index,
                id: `${trede}_${index}`
              };
              allQuestions.push(mappedQuestion);
            });
          }
        }
      }

      // Sort questions by trede order
      allQuestions.sort((a, b) => {
        if (a.tredeIndex !== b.tredeIndex) {
          return a.tredeIndex - b.tredeIndex;
        }
        return a.questionIndex - b.questionIndex;
      });

      setQuestions(allQuestions);
      console.log('✅ Total questions loaded:', allQuestions.length);
      
    } catch (err) {
      console.error('Error loading quiz data:', err);
      setError('Er is een fout opgetreden bij het laden van de RelatieScan.');
    } finally {
      setLoading(false);
    }
  };

  // ✅ FALLBACK: Legacy search for old invitations (before index was added)
  const legacyInviteSearch = async (inviteCode) => {
    try {
      console.log('🔄 Running legacy search for:', inviteCode);
      
      const teamsSnapshot = await getDocs(collection(db, 'teams'));
      console.log('📊 Searching', teamsSnapshot.docs.length, 'teams (legacy)');

      for (const teamDoc of teamsSnapshot.docs) {
        const teamId = teamDoc.id;
        
        // Search in this team's invites subcollection
        const invitesRef = collection(db, 'teams', teamId, 'invites');
        const inviteQuery = query(invitesRef, where('code', '==', inviteCode));
        const inviteSnapshot = await getDocs(inviteQuery);
        
        if (!inviteSnapshot.empty) {
          const inviteDoc = inviteSnapshot.docs[0];
          const inviteData = inviteDoc.data();
          
          console.log('✅ Found in legacy search:', inviteData);
          
          return {
            teamId: teamId,
            inviteId: inviteDoc.id,
            code: inviteCode
          };
        }
      }
      
      console.log('❌ Not found in legacy search');
      return null;
      
    } catch (error) {
      console.error('Error in legacy search:', error);
      return null;
    }
  };

  const handleAnswer = (questionId, answer) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const goToNextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      submitQuiz();
    }
  };

  const goToPreviousQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const submitQuiz = async () => {
    try {
      setIsSubmitting(true);

      // Calculate scores per trede
      const tredeScores = {};
      tredes.forEach(trede => {
        const tredeQuestions = questions.filter(q => q.trede === trede);
        const tredeResponses = tredeQuestions.map(q => responses[q.id]).filter(r => r !== undefined);
        
        if (tredeResponses.length > 0) {
          const average = tredeResponses.reduce((sum, response) => sum + response, 0) / tredeResponses.length;
          tredeScores[trede] = Math.round(average * 100) / 100; // Round to 2 decimals
        }
      });

      // Save response to database
      const responseData = {
        teamId: invitation.teamId,
        invitationId: invitation.id,
        email: invitation.email,
        responses: responses,
        tredeScores: tredeScores,
        completedAt: new Date(),
        inviteCode: inviteCode
      };

      await addDoc(collection(db, 'responses'), responseData);

      // Mark invitation as used in subcollection
      const inviteRef = doc(db, 'teams', invitation.teamId, 'invites', invitation.id);
      await updateDoc(inviteRef, {
        used: true,
        completedAt: new Date()
      });

      console.log('✅ Quiz completed and invitation marked as used');
      setQuizCompleted(true);

    } catch (err) {
      console.error('Error submitting quiz:', err);
      setError('Er is een fout opgetreden bij het opslaan van je antwoorden.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getProgressPercentage = () => {
    return Math.round(((currentQuestion + 1) / questions.length) * 100);
  };

  const getCurrentTrede = () => {
    if (questions.length === 0) return null;
    return questions[currentQuestion]?.trede;
  };

  const isCurrentQuestionAnswered = () => {
    const currentQ = questions[currentQuestion];
    return currentQ && responses[currentQ.id] !== undefined;
  };

  // Loading state
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #e5e7eb',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#6b7280' }}>RelatieScan wordt geladen...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          padding: '32px',
          maxWidth: '400px',
          width: '100%',
          margin: '16px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '48px',
              height: '48px',
              background: '#fef2f2',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <svg style={{ width: '24px', height: '24px', color: '#dc2626' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: '500', color: '#1f2937', marginBottom: '8px' }}>
              Oeps!
            </h3>
            <p style={{ color: '#6b7280', marginBottom: '24px' }}>{error}</p>
            <button
              onClick={() => navigate('/')}
              style={{
                width: '100%',
                background: '#3b82f6',
                color: 'white',
                padding: '12px 16px',
                borderRadius: '8px',
                border: 'none',
                fontSize: '16px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
            >
              Terug naar Homepage
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Quiz completed state
  if (quizCompleted) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          padding: '32px',
          maxWidth: '400px',
          width: '100%',
          margin: '16px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '48px',
              height: '48px',
              background: '#f0fdf4',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <svg style={{ width: '24px', height: '24px', color: '#16a34a' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: '500', color: '#1f2937', marginBottom: '8px' }}>
              Bedankt!
            </h3>
            <p style={{ color: '#6b7280', marginBottom: '16px' }}>
              Je hebt de Relatiescan succesvol voltooid.
            </p>
            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px' }}>
              Je therapeut ontvangt de resultaten zodra iedereen de RealtieScan heeft ingevuld.
            </p>
            <p style={{ fontSize: '16px', color: '#374151', fontWeight: '500', textAlign: 'center' }}>
              Je kunt deze pagina nu sluiten.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Quiz interface
  if (questions.length === 0) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: '#6b7280' }}>Geen vragen gevonden.</p>
        </div>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];
  const currentTrede = getCurrentTrede();

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)'
    }}>
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '16px'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px', paddingTop: '32px' }}>
          <h1 style={{
            fontSize: '32px',
            fontWeight: 'bold',
            color: '#1f2937',
            marginBottom: '8px'
          }}>
            Lencioni RelatieScan
          </h1>
          <p style={{ color: '#6b7280' }}>Relatie: {teamInfo?.name}</p>
        </div>

        {/* Progress Bar */}
        <div style={{
          maxWidth: '600px',
          margin: '0 auto 32px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '14px',
            color: '#6b7280',
            marginBottom: '8px'
          }}>
            <span>Voortgang</span>
            <span>{currentQuestion + 1} van {questions.length}</span>
          </div>
          <div style={{
            width: '100%',
            background: '#e5e7eb',
            borderRadius: '4px',
            height: '8px'
          }}>
            <div style={{
              background: '#3b82f6',
              height: '8px',
              borderRadius: '4px',
              transition: 'width 0.3s ease',
              width: `${getProgressPercentage()}%`
            }}></div>
          </div>
          <div style={{ textAlign: 'center', marginTop: '8px' }}>
            <span style={{
              fontSize: '12px',
              background: '#dbeafe',
              color: '#1e40af',
              padding: '4px 12px',
              borderRadius: '12px'
            }}>
              {tredeNames[currentTrede]}
            </span>
          </div>
        </div>

        {/* Question Card */}
        <div style={{
          maxWidth: '600px',
          margin: '0 auto'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            padding: '32px'
          }}>
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#1f2937',
                marginBottom: '16px',
                lineHeight: '1.4'
              }}>
                {currentQ.vraag}
              </h2>
            </div>

            {/* Answer Options */}
            <div style={{ marginBottom: '32px' }}>
              {currentQ.type === 'schaal' ? (
                // Scale questions (1-2-4-5)
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {[1, 2, 4, 5].map(value => (
                    <label 
                      key={value}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '16px',
                        border: `2px solid ${responses[currentQ.id] === value ? '#3b82f6' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        background: responses[currentQ.id] === value ? '#eff6ff' : 'white'
                      }}
                      onMouseOver={(e) => {
                        if (responses[currentQ.id] !== value) {
                          e.target.style.borderColor = '#d1d5db';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (responses[currentQ.id] !== value) {
                          e.target.style.borderColor = '#e5e7eb';
                        }
                      }}
                    >
                      <input
                        type="radio"
                        name={currentQ.id}
                        value={value}
                        checked={responses[currentQ.id] === value}
                        onChange={(e) => handleAnswer(currentQ.id, parseInt(e.target.value))}
                        style={{
                          width: '16px',
                          height: '16px',
                          accentColor: '#3b82f6'
                        }}
                      />
                      <span style={{ 
                        marginLeft: '12px', 
                        fontWeight: '500', 
                        color: '#1f2937' 
                      }}>
                        {value}
                      </span>
                      <span style={{ 
                        marginLeft: '8px', 
                        color: '#6b7280' 
                      }}>
                        {value === 1 && '- Volledig mee oneens'}
                        {value === 2 && '- Mee oneens'}
                        {value === 4 && '- Mee eens'}
                        {value === 5 && '- Volledig mee eens'}
                      </span>
                    </label>
                  ))}
                </div>
              ) : (
                // Scenario questions with multiple choice
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {currentQ.opties?.map((optie, index) => (
                    <label 
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        padding: '16px',
                        border: `2px solid ${responses[currentQ.id] === optie.waarde ? '#3b82f6' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        background: responses[currentQ.id] === optie.waarde ? '#eff6ff' : 'white'
                      }}
                      onMouseOver={(e) => {
                        if (responses[currentQ.id] !== optie.waarde) {
                          e.target.style.borderColor = '#d1d5db';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (responses[currentQ.id] !== optie.waarde) {
                          e.target.style.borderColor = '#e5e7eb';
                        }
                      }}
                    >
                      <input
                        type="radio"
                        name={currentQ.id}
                        value={optie.waarde}
                        checked={responses[currentQ.id] === optie.waarde}
                        onChange={(e) => handleAnswer(currentQ.id, parseInt(e.target.value))}
                        style={{
                          width: '16px',
                          height: '16px',
                          marginTop: '2px',
                          accentColor: '#3b82f6'
                        }}
                      />
                      <span style={{ 
                        marginLeft: '12px', 
                        color: '#1f2937',
                        lineHeight: '1.4'
                      }}>
                        {optie.tekst}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Navigation Buttons */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between'
            }}>
              <button
                onClick={goToPreviousQuestion}
                disabled={currentQuestion === 0}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: currentQuestion === 0 ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.2s',
                  background: currentQuestion === 0 ? '#f3f4f6' : '#e5e7eb',
                  color: currentQuestion === 0 ? '#9ca3af' : '#374151'
                }}
                onMouseOver={(e) => {
                  if (currentQuestion !== 0) {
                    e.target.style.backgroundColor = '#d1d5db';
                  }
                }}
                onMouseOut={(e) => {
                  if (currentQuestion !== 0) {
                    e.target.style.backgroundColor = '#e5e7eb';
                  }
                }}
              >
                Vorige
              </button>

              <button
                onClick={goToNextQuestion}
                disabled={!isCurrentQuestionAnswered() || isSubmitting}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: (!isCurrentQuestionAnswered() || isSubmitting) ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.2s',
                  background: (!isCurrentQuestionAnswered() || isSubmitting) ? '#f3f4f6' : '#3b82f6',
                  color: (!isCurrentQuestionAnswered() || isSubmitting) ? '#9ca3af' : 'white',
                  display: 'flex',
                  alignItems: 'center'
                }}
                onMouseOver={(e) => {
                  if (isCurrentQuestionAnswered() && !isSubmitting) {
                    e.target.style.backgroundColor = '#2563eb';
                  }
                }}
                onMouseOut={(e) => {
                  if (isCurrentQuestionAnswered() && !isSubmitting) {
                    e.target.style.backgroundColor = '#3b82f6';
                  }
                }}
              >
                {isSubmitting ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid transparent',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      marginRight: '8px'
                    }}></div>
                    Versturen...
                  </>
                ) : currentQuestion === questions.length - 1 ? (
                  'RelatieScan Voltooien'
                ) : (
                  'Volgende'
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Question Counter */}
        <div style={{
          textAlign: 'center',
          marginTop: '24px',
          fontSize: '14px',
          color: '#6b7280'
        }}>
          Vraag {currentQuestion + 1} van {questions.length}
        </div>
      </div>

      {/* CSS Animations */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default QuizComponent;